import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  Box,
  Typography,
  Chip,
  Collapse,
  Button,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleIcon from '@mui/icons-material/People';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import ClassificacaoBadge, { getClassificacaoColor } from './ClassificacaoBadge';
import ProgressBarWithMetas from './ProgressBarWithMetas';

interface EquipeIndicador {
  indicador: string;
  nome?: string;
  numerador: number;
  denominador: number;
  percentual: number;
  pontuacao?: number;
  peso?: number;
}

interface EquipeCardData {
  ine: string;
  equipe: string;
  unidade: string;
  total_identificados: number;
  indicadores: EquipeIndicador[];
  score: number;
  classificacao: string;
}

interface EquipeCardProps {
  equipe: EquipeCardData;
  indicadoresNomes?: Record<string, string>;
  mediaIndicadores?: number[];
  onVerPacientes?: (ine: string) => void;
  defaultExpanded?: boolean;
}

const getIndicadorLabel = (indicador: string): string => {
  const parts = indicador.split('_');
  return parts[parts.length - 1]?.toUpperCase() || indicador;
};

const getIndicadorColor = (pct: number): string => {
  if (pct >= 75) return '#2e7d32';
  if (pct >= 50) return '#1565c0';
  if (pct >= 25) return '#e65100';
  return '#c62828';
};

const EquipeCard: React.FC<EquipeCardProps> = ({
  equipe,
  indicadoresNomes,
  mediaIndicadores,
  onVerPacientes,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const radarData = equipe.indicadores.map((ind, idx) => ({
    subject: indicadoresNomes?.[ind.indicador] || ind.nome || getIndicadorLabel(ind.indicador),
    equipe: ind.percentual,
    media: mediaIndicadores?.[idx] ?? 0,
  }));

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      {/* Collapsed header */}
      <CardActionArea onClick={() => setExpanded((prev) => !prev)} sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Left: name + unidade + count */}
          <Box sx={{ minWidth: 160, flex: '0 0 auto' }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {equipe.equipe}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {equipe.unidade}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {equipe.total_identificados} identificados
            </Typography>
          </Box>

          {/* Center: indicator chips */}
          <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
            {equipe.indicadores.map((ind) => {
              const label = getIndicadorLabel(ind.indicador);
              const color = getIndicadorColor(ind.percentual);
              return (
                <Chip
                  key={ind.indicador}
                  label={`${label}:${ind.percentual.toFixed(0)}%`}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 22,
                    backgroundColor: alpha(color, 0.1),
                    color,
                    fontWeight: 600,
                  }}
                />
              );
            })}
          </Box>

          {/* Right: badge + score + expand icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <ClassificacaoBadge classificacao={equipe.classificacao} variant="chip" size="small" />
            <Typography variant="body2" fontWeight={700}>
              {equipe.score.toFixed(1)}
            </Typography>
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.25s ease',
                color: 'text.secondary',
              }}
            />
          </Box>
        </Box>
      </CardActionArea>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            px: 2,
            pb: 2,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
          }}
        >
          {/* Radar chart */}
          <Box sx={{ width: isMobile ? '100%' : '60%', minHeight: 260 }}>
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={radarData}>
                <PolarGrid strokeDasharray="3 3" />
                {/* @ts-ignore recharts type compatibility */}
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(val: any) => [`${Number(val).toFixed(1)}%`]} />
                {mediaIndicadores && (
                  <Radar
                    name="Media Municipal"
                    dataKey="media"
                    stroke="#9e9e9e"
                    fill="#9e9e9e"
                    fillOpacity={0.1}
                    strokeDasharray="5 5"
                  />
                )}
                <Radar
                  name="Equipe"
                  dataKey="equipe"
                  stroke="#1565c0"
                  fill="#1565c0"
                  fillOpacity={0.2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </Box>

          {/* Indicators list */}
          <Box sx={{ width: isMobile ? '100%' : '40%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {equipe.indicadores.map((ind) => {
              const nome = indicadoresNomes?.[ind.indicador] || ind.nome || getIndicadorLabel(ind.indicador);
              const color = getIndicadorColor(ind.percentual);
              const faltam = ind.denominador - ind.numerador;
              return (
                <Box key={ind.indicador}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1 }}>
                      {nome}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color, ml: 1 }}>
                      {ind.percentual.toFixed(1)}%
                    </Typography>
                  </Box>
                  <ProgressBarWithMetas value={ind.percentual} color={color} height={6} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                    {ind.numerador}/{ind.denominador} &mdash; faltam {faltam > 0 ? faltam : 0}
                  </Typography>
                </Box>
              );
            })}

            {onVerPacientes && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PeopleIcon />}
                onClick={() => onVerPacientes(equipe.ine)}
                sx={{ mt: 1, alignSelf: 'flex-start' }}
              >
                Ver pacientes desta equipe
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
};

export default EquipeCard;
